import { Alert } from '../../atoms/Alert';
import { Button } from '../../atoms/Button';
import { Chip } from '../../atoms/Chip';

import InnerHTMLContainer from '../../molecules/InnerHTMLContainer';
import Player from '../../molecules/Player';

import { ButtonGroup } from '../ButtonGroup';
import Comment from '../comment';

import { Fragment, useEffect } from 'react';
import { useUserStore } from '../../../../modules/Auth/stores/authStore';
import { useExerciseListHook } from '../../../hooks/exerciseList';
import {
    ExerciseAction,
    IExercise,
    IExerciseAlternative,
} from '../../../models/exercise';
import { IVideo } from '../../../models/video';
import { copyText } from '../../../utils';
import { getDisciplineName } from '../../../utils/new_discipline';
import { getVideoConfig } from '../../../utils/video';
import ExerciseAlternative from '../../molecules/ExerciseAlternative';
import { Typography } from '../../molecules/Typography';
import { Modal } from '../../templates/Modal';
import { ContentActions } from '../ContentActions';
import Lock from '../Lock';

interface ExerciseListProps {
  data: IExercise[];
  isLoading?: boolean;
  tryAgainEnabled?: boolean;
  onExerciseChange: (exercise: IExercise, action?: ExerciseAction) => void;
}

export const ExerciseList = ({
                               data,
                               tryAgainEnabled = true,
                               isLoading = false,
                               onExerciseChange,
                             }: ExerciseListProps) => {
  const {
    showVideo,
    isModalOpen,
    exerciseTypes,
    tryAgain,
    viewAnswer,
    setModalOpen,
    setShowVideo,
    onFavorite,
    setExerciseAlternative,
    convertMathGraphs,
  } = useExerciseListHook();

  const { user } = useUserStore();

  const exercise =
    data.filter((exercise: IExercise) => exercise.selected)[0] ??
    ({} as IExercise);

  const resolutionVideo = exercise.resolution_video as IVideo;

  const { playerType, token } = getVideoConfig(resolutionVideo);

  useEffect(() => {
    convertMathGraphs();
  });

  if(exercise.ancestors.level.slug !== 'superior') {
    return <Lock />
  }

  return (
    <div key={exercise.id} className="flex w-full flex-col gap-xxl">
      <div className="flex flex-col gap-lg">
        <div className="flex h-fit flex-col gap-sm">
          <div className="flex items-center gap-lg">
            {exercise.ancestors && exercise.ancestors.area && (
              <Typography size="small" className="text-mono-color-darker">
                {getDisciplineName(exercise.ancestors.area.slug)}
              </Typography>
            )}
            {typeof exercise.subjects === 'string' ? (
              <Typography size="small" className="text-mono-color-darker">
                {exercise.subjects}
              </Typography>
            ) : Array.isArray(exercise.subjects) ? (
              exercise.subjects && (
                <Typography size="small" className="text-mono-color-darker">
                  {exercise.subjects.map((subject: { title: string }) => subject.title).join(', ')}
                </Typography>
              )
            ) : null}
          </div>
          <div className="flex flex-col justify-between gap-md md:flex-row md:items-center">
            <div className="flex gap-sm">
              <Chip variant="negative">{`Esta resposta é ${
                exerciseTypes[exercise.exerciseType]
              }`}</Chip>
              {user?.exercisePercentage &&
                exercise.exerciseType !== 'discursive' &&
                exercise.correctPercentage && (
                  <Chip variant="negative">{`${(
                    exercise.correctPercentage * 100
                  )
                    .toFixed(2)
                    .toString()}% de acerto`}</Chip>
                )}
            </div>

            <ContentActions.Root
              contentType={'Exercise'}
              contentId={exercise.id}
              className={'ml-auto'}
            >
              <ContentActions.FavoriteButton
                defaultFavorite={exercise.favorite}
                category={'exercise'}
                onChange={(isFavorite) => {
                  onExerciseChange(onFavorite(exercise, isFavorite));
                }}
              />
              <ContentActions.Report />
            </ContentActions.Root>
          </div>
        </div>
        <InnerHTMLContainer
          data={exercise.statement}
          type="courseProgram"
          className="hyphens-auto text-mono-color-darkest xs:break-keep"
        />
        <Typography
          size="mini"
          className="cursor-pointer text-right"
          onClick={() => copyText(exercise.id)}
        >
          Código: {exercise.id}
        </Typography>
      </div>
      <div className="flex flex-col gap-md">
        {exercise.exerciseType === 'discursive' ? (
          <>
            <Alert
              variant="warning"
              message="Desenvolva a questão em seu caderno e depois clique no botão VER RESPOSTA"
            />
            {exercise.showAnswer && (
              <>
                <div className="ml-10 rounded-md border-hairline border-mono-color-darkest p-5">
                  <InnerHTMLContainer
                    data={exercise.resolution}
                    type="courseProgram"
                    className="text-mono-color-darkest"
                  />
                </div>
                <div className="flex justify-end">
                  <Button
                    variant="outlined"
                    onClick={() => {
                      onExerciseChange({
                        ...exercise,
                        correct: true,
                        done: true
                      }, 'verifyAnswer');
                    }}
                  >
                    Marcar como correta
                  </Button>
                </div>
              </>
            )}
          </>
        ) : (
          exercise.alternatives?.map((alternative: IExerciseAlternative) => {
            return (
              <Fragment key={alternative.id}>
                <ExerciseAlternative
                  alternativeId={alternative.id}
                  text={alternative.text}
                  value={alternative.text}
                  type={exercise.exerciseType}
                  selected={alternative.selected}
                  status={alternative.status}
                  isLoading={isLoading}
                  disabled={exercise.done}
                  onSelected={() => {
                    onExerciseChange(
                      setExerciseAlternative(alternative.id, exercise),
                      'selectAlternative',
                    );
                  }}
                />
                {exercise.showAnswer && alternative.status === 'correct' && (
                  <div className="ml-10 rounded-md border-hairline border-mono-color-medium p-5">
                    <InnerHTMLContainer
                      data={exercise.resolution}
                      type="courseProgram"
                      className="text-mono-color-darkest"
                    />
                  </div>
                )}
              </Fragment>
            );
          })
        )}
      </div>
      {(exercise.correct !== undefined ||
        exercise.exerciseType === 'discursive') && (
        <>
          {exercise.correct && (
            <Alert
              title="Resposta correta 😎"
              message="Parabéns!"
              variant="success"
            />
          )}
          {!exercise.correct && exercise.exerciseType !== 'discursive' && (
            <Alert
              title="Resposta incorreta 😔"
              message="Tente novamente ou veja a resposta correta"
              variant="error"
            />
          )}
          <div className="flex flex-col items-center md:flex-row md:justify-between">
            <Button
              LeftIcon={exercise.showAnswer ? 'visibility_off' : 'visibility'}
              variant="text"
              onClick={() => {
                onExerciseChange(viewAnswer(exercise), 'showAnswer');
              }}
            >
              {exercise.showAnswer ? 'Ocultar resposta' : 'Ver resposta'}
            </Button>
            {/* {hasToken && (
              <Button
                LeftIcon="videocam"
                variant="outlined"
                onClick={() => {
                  setShowVideo(true);
                  setModalOpen(true);
                }}
              >
                Assistir resolução em vídeo
              </Button>
            )} */}
            {tryAgainEnabled && (
              <Button
                LeftIcon="refresh"
                variant="text"
                disabled={exercise.exerciseType === 'discursive'}
                onClick={() => {
                  onExerciseChange(tryAgain(exercise));
                }}
              >
                Tentar novamente
              </Button>
            )}
          </div>
        </>
      )}
      <Modal
        title="Resolução do exercício"
        isOpen={isModalOpen}
        onClose={() => {
          setModalOpen(false);
        }}
      >
        <div className="flex flex-col gap-xl">
          <ButtonGroup
            buttons={[
              {
                label: 'Resolução em vídeo',
                selected: showVideo,
                onClick: () => setShowVideo(true),
              },
              {
                label: 'Comentários',
                selected: !showVideo,
                onClick: () => setShowVideo(false),
              },
            ]}
          />

          {showVideo ? (
            <Player
              token={token}
              playerType={playerType}
              initialTime={0}
              className="h-[48vh] w-full"
            />
          ) : (
            <Comment
              title="Comentários da videoaula"
              targetId={exercise.id}
              targetType="Exercise"
              isModal
            />
          )}
        </div>
      </Modal>
    </div>
  );
};
